import { SeatPassangerEstatus } from '@app/enums';
import { CartInt } from '@app/interfaces/micro/cart.interface';
import { SeatPassanger } from '@app/interfaces/passenger.interface';
import { Passenger } from '@app/models/micro/passenger.model';

export interface SeatsStepValidation {
  valid: boolean;
  reason?: string;
}

const VALID_STEP: SeatsStepValidation = { valid: true };

/** Entrada: hay seleccion de pasajeros y todos traen el identificador que consumen los micros. */
export function passengersReadyForSeats(passengers: Passenger[]): SeatsStepValidation {
  if (!passengers?.length) {
    return { valid: false, reason: 'NO_PASSENGERS' };
  }
  const incomplete = passengers.some((pax) => !pax?.nameRefNumber);
  return incomplete ? { valid: false, reason: 'PASSENGER_WITHOUT_NAME_NUMBER' } : VALID_STEP;
}

/** Oferta: el seatmap del segmento llego y trae asientos (getSeatMaps devuelve [] en un 200 vacio). */
export function seatMapReadyForSegment(
  seatMaps: unknown[],
  segmentCode: string
): SeatsStepValidation {
  if (!seatMaps?.length) {
    return { valid: false, reason: 'EMPTY_SEATMAP' };
  }
  const segmentSeatMap = (
    seatMaps as { segment?: { segmentCode?: string }; seatMap?: unknown }[]
  ).find((map) => map?.segment?.segmentCode === segmentCode);
  if (!segmentSeatMap) {
    return { valid: false, reason: 'SEGMENT_NOT_IN_SEATMAP' };
  }
  const hasSeats =
    !!segmentSeatMap.seatMap && Object.keys(segmentSeatMap.seatMap as object).length > 0;
  return hasSeats ? VALID_STEP : { valid: false, reason: 'SEATMAP_WITHOUT_SEATS' };
}

/**
 * Guardado (chequeo de INTEGRIDAD, no de obligatoriedad): todo asiento nuevo quedo
 * con codigo y estatus resueltos por /ac/seat/v3.
 * Sin `segmentCode` valida todos los asientos nuevos (pantalla de revision, que lista
 * varios segmentos); con `segmentCode` valida solo los de ese segmento.
 *
 * Caso lista vacia (el pasajero NO selecciono asiento): es un estado VALIDO a proposito.
 * En kiosko elegir asiento es opcional; cuando no hay asientos nuevos el flujo de
 * `onContinue` NO navega a pago (eso solo ocurre con `hasSeatToPay`) sino a
 * boarding passes / menu de CKI, y la guarda real contra "carrito vacio" vive en
 * `cartReadyForSeatsPayment`. Esta funcion solo detecta asientos a medio persistir,
 * por eso una lista vacia (nada que validar) se considera integra -> VALID_STEP.
 */
export function seatsPersistedForSegment(
  passengers: Passenger[],
  segmentCode?: string
): SeatsStepValidation {
  const seatsOfSegment: SeatPassanger[] = (passengers ?? []).flatMap((pax) =>
    (pax?.seatsNew ?? []).filter((seat) => !segmentCode || seat?.segmentCode === segmentCode)
  );
  const incomplete = seatsOfSegment.some(
    (seat) => !(seat?.code || seat?.seatCode) || seat?.status === undefined || seat?.status === null
  );
  return incomplete ? { valid: false, reason: 'SEAT_NOT_CONFIRMED' } : VALID_STEP;
}

/** Pago: si hay asientos en PENDING_PAYMENT el carrito debe traer la linea de asientos con importe. */
export function cartReadyForSeatsPayment(
  cart: CartInt,
  passengers: Passenger[]
): SeatsStepValidation {
  const hasSeatsToPay = (passengers ?? []).some((pax) =>
    (pax?.seatsNew?.length ? pax.seatsNew : pax?.seats ?? []).some(
      (seat) => seat?.status === SeatPassangerEstatus.PENDING_PAYMENT
    )
  );
  if (!hasSeatsToPay) {
    return VALID_STEP;
  }
  if (!cart?.items?.length) {
    return { valid: false, reason: 'EMPTY_CART' };
  }
  const seatItem = cart.items.find((item) => item.typeAncillarie === 'seats');
  return seatItem && seatItem.subtotal > 0
    ? VALID_STEP
    : { valid: false, reason: 'CART_WITHOUT_SEAT_ITEM' };
}
